import React, { type ReactElement } from 'react';
import SystemErrorAlert from 'feature-components/system-error-alert';
import {
  friendlyResponseStatus,
  isResponseReadyToBeMarked
} from 'helpers/domain-specific/response-status.helpers';
import { joinWithConjunction } from 'helpers/string.helpers';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import {
  ResponseStatus,
  type TestInstanceResponseWithResponseItemsAndMediaObjects
} from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import type { TestRubricWithMediaObjects } from 'services/assist-api/entities/test-rubric/types';

type Props = {
  requiredMarkingData: {
    response: TestInstanceResponseWithResponseItemsAndMediaObjects | undefined;
    rubric: TestRubricWithMediaObjects | undefined;
    assessment: BaseTestInstance | undefined;
    currentOrganisationUser: MeCustomerUser | undefined;
  };
  markStatus?: MarkStatus;
};

export default function CannotMarkMessages({
  requiredMarkingData,
  markStatus
}: Props): ReactElement {
  const responseNotReadyToBeMarkedMessage = `Response does not have a ${joinWithConjunction(
    [ResponseStatus.Finished, ResponseStatus.Closed].map(
      (responseStatus) => `"${friendlyResponseStatus(responseStatus)}"`
    ),
    'or'
  )} status, so it can't be marked.`;

  const responseReadyToBeMarked = isResponseReadyToBeMarked(requiredMarkingData.response?.status);

  return (
    <>
      {/**
       * Omit the system error if mark status is Response Reopened so that the user can view the
       * existing mark (with disabled form fields) while further changes are being made to the
       * response. The Mark Status badge will provide additional information about this status.
       */}
      {!responseReadyToBeMarked && markStatus !== MarkStatus.ResponseReopened && (
        <SystemErrorAlert>{responseNotReadyToBeMarkedMessage}</SystemErrorAlert>
      )}
      {!requiredMarkingData.response && (
        <SystemErrorAlert>
          No response found for the response ID specified in the URL, so it can't be marked.
        </SystemErrorAlert>
      )}
      {!requiredMarkingData.rubric && (
        <SystemErrorAlert>No rubric found for this test, so it can't be marked.</SystemErrorAlert>
      )}
      {!requiredMarkingData.assessment && (
        <SystemErrorAlert>
          No assessment found for this response, so it can't be marked.
        </SystemErrorAlert>
      )}
      {!requiredMarkingData.currentOrganisationUser && (
        <SystemErrorAlert>
          No organisation user found for the current user, so marking cannot take place.
        </SystemErrorAlert>
      )}
    </>
  );
}
